import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Link2, UploadCloud, Copy, Check, X, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  ingestImageFromUrl,
  finaliseUploadedImage,
  bulkAttachImages,
  type AttachEntity,
  type AttachResult,
} from "@/lib/admin/bulk-images.functions";
import {
  downloadCatalogueTemplate,
  exportCatalogue,
  type Entity,
} from "@/lib/admin/bulk-export.functions";
import {
  previewBulkImport,
  applyBulkImport,
  discardBulkImport,
  type PreviewResult,
  type ApplyResult,
  type ImportEntity,
} from "@/lib/admin/bulk-import.functions";
import { createSignedUploadUrl } from "@/lib/storage/signed-urls.functions";
import { useResolvedImageUrl } from "@/hooks/useResolvedImageUrls";
import { Download, FileSpreadsheet, FileUp, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_admin/admin/bulk")({
  component: BulkDataPage,
});

type IngestResult = {
  bucket: string;
  object_key: string;
  storage_ref: string;
  bytes: number;
  content_type: string;
  width: number;
  height: number;
};

function BulkDataPage() {
  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Bulk Data"
        description="Bulk export, import and shared image tools for the catalogue. Only Images is active in the current phase."
      />
      <Tabs defaultValue="images" className="w-full">
        <TabsList>
          <TabsTrigger value="export">Export</TabsTrigger>
          <TabsTrigger value="import">Import</TabsTrigger>
          <TabsTrigger value="images">Images</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="export" className="mt-4">
          <ExportTab />
        </TabsContent>
        <TabsContent value="import" className="mt-4">
          <ImportTab />
        </TabsContent>
        <TabsContent value="images" className="mt-4">
          <ImagesTab />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <ComingSoon
            title="Import history"
            description="Audit log of all bulk operations with per-row diagnostics. Ships in a later phase."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ComingSoon({ title, description }: { title: string; description: string }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle>{title}</CardTitle>
          <Badge variant="outline">Coming later</Badge>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
    </Card>
  );
}

function ImagesTab() {
  return (
    <div className="space-y-4">
      <BatchAttachCard />
      <div className="grid gap-4 md:grid-cols-2">
        <UrlIngestCard />
        <FileUploadCard />
      </div>
    </div>
  );
}

const ATTACH_ENTITIES: Array<{ id: AttachEntity; label: string; key: string }> = [
  { id: "dishes", label: "Meal Plan Dishes", key: "slug" },
  { id: "beverages", label: "Beverages", key: "slug" },
  { id: "soups", label: "Soups", key: "slug" },
  { id: "salads", label: "Salads", key: "slug" },
  { id: "products", label: "Pantry & Gifting", key: "code" },
];

const MAX_BATCH = 25;

function BatchAttachCard() {
  const reserve = useServerFn(createSignedUploadUrl);
  const attach = useServerFn(bulkAttachImages);
  const [entity, setEntity] = useState<AttachEntity>("dishes");
  const [files, setFiles] = useState<File[]>([]);
  const [results, setResults] = useState<AttachResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const keyField = ATTACH_ENTITIES.find((e) => e.id === entity)?.key ?? "slug";

  async function run() {
    if (files.length === 0) return;
    setBusy(true);
    setResults(null);
    const uploaded: Array<{ entity: AttachEntity; key: string; object_key: string; filename: string }> = [];
    const failures: AttachResult[] = [];
    try {
      for (const file of files) {
        const base = file.name.replace(/\.[^.]+$/, "");
        const key = base.trim().toLowerCase();
        if (file.size > 8 * 1024 * 1024) {
          failures.push({ filename: file.name, entity, key, status: "failed", reason: "exceeds_8mb" });
          continue;
        }
        try {
          const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
          const now = new Date();
          const objKey = `${now.getUTCFullYear()}/${String(now.getUTCMonth() + 1).padStart(2, "0")}/${crypto.randomUUID().replace(/-/g, "")}.${ext}`;
          const signed = await reserve({ data: { bucket: "bulk-images", object_key: objKey } });
          const put = await fetch(signed.signed_url, {
            method: "PUT",
            headers: { "content-type": file.type || "application/octet-stream" },
            body: file,
          });
          if (!put.ok) throw new Error(`upload_http_${put.status}`);
          uploaded.push({ entity, key, object_key: signed.path, filename: file.name });
        } catch (err) {
          failures.push({
            filename: file.name,
            entity,
            key,
            status: "failed",
            reason: err instanceof Error ? err.message : "upload_failed",
          });
        }
      }

      let attached: AttachResult[] = [];
      if (uploaded.length > 0) {
        const res = await attach({ data: { items: uploaded } });
        attached = res.results;
      }
      const all = [...attached, ...failures];
      setResults(all);
      const ok = all.filter((r) => r.status !== "failed").length;
      const bad = all.length - ok;
      if (bad === 0) toast.success(`Attached ${ok} image${ok === 1 ? "" : "s"}`);
      else toast.error(`${ok} attached · ${bad} failed`);
    } catch (err) {
      toast.error(`Batch failed: ${err instanceof Error ? err.message : "unknown_error"}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <UploadCloud className="h-4 w-4" aria-hidden />
          <CardTitle>Batch attach to catalogue</CardTitle>
        </div>
        <CardDescription>
          Select up to {MAX_BATCH} images. Each filename (without extension) must
          match the target <code className="mx-1">{keyField}</code> — e.g.
          <code className="mx-1">masala-oats.jpg</code>. PNG, JPEG or WebP · min
          200 × 200 · max 8 MB. Items are attached independently: a rejected file
          is deleted from storage and never leaves a broken reference.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-[220px_1fr]">
          <div className="space-y-1.5">
            <Label htmlFor="batch-entity">Module</Label>
            <select
              id="batch-entity"
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={entity}
              onChange={(e) => {
                setEntity(e.target.value as AttachEntity);
                setResults(null);
              }}
              disabled={busy}
            >
              {ATTACH_ENTITIES.map((e) => (
                <option key={e.id} value={e.id}>{e.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="batch-files">Images</Label>
            <Input
              id="batch-files"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                setFiles(Array.from(e.target.files ?? []).slice(0, MAX_BATCH));
                setResults(null);
              }}
              disabled={busy}
            />
          </div>
        </div>
        <Button onClick={run} disabled={busy || files.length === 0}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          Upload &amp; attach {files.length > 0 ? `(${files.length})` : ""}
        </Button>
        {results && <BatchResultTable results={results} />}
      </CardContent>
    </Card>
  );
}

function BatchResultTable({ results }: { results: AttachResult[] }) {
  return (
    <div className="overflow-auto rounded-md border">
      <table className="w-full text-xs">
        <thead className="bg-muted/50">
          <tr>
            <th className="p-2 text-left">Preview</th>
            <th className="p-2 text-left">File</th>
            <th className="p-2 text-left">Target</th>
            <th className="p-2 text-left">Status</th>
            <th className="p-2 text-left">Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <BatchResultRow key={`${r.filename}-${r.key}`} result={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BatchResultRow({ result }: { result: AttachResult }) {
  const url = useResolvedImageUrl(result.storage_ref ?? null);
  return (
    <tr className="border-t">
      <td className="p-2">
        {url ? (
          <img src={url} alt={`${result.key} thumbnail`} className="h-12 w-12 rounded object-cover" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded bg-muted">
            <ImageOff className="h-4 w-4 text-muted-foreground" aria-hidden />
          </div>
        )}
      </td>
      <td className="p-2 align-middle">{result.filename}</td>
      <td className="p-2 align-middle font-mono">{result.entity}/{result.key}</td>
      <td className="p-2 align-middle">
        <Badge variant={result.status === "failed" ? "destructive" : "outline"} className="text-[10px]">
          {result.status}
        </Badge>
      </td>
      <td className="p-2 align-middle text-red-700">
        {result.reason ??
          (result.width
            ? `${result.width}×${result.height} · ${Math.round((result.bytes ?? 0) / 1024)} KB`
            : "")}
      </td>
    </tr>
  );
}

const IMPORT_ENTITIES: Array<{ id: ImportEntity; label: string; note: string; updateOnly: boolean }> = [
  { id: "dishes", label: "Meal Plan Dishes", note: "Key: slug. Supports create + update.", updateOnly: false },
  { id: "beverages", label: "Beverages", note: "Key: id (UUID) — update only. Fill id from an export.", updateOnly: true },
  { id: "soups", label: "Soups", note: "Key: id (UUID) — update only. Fill id from an export.", updateOnly: true },
  { id: "salads", label: "Salads", note: "Key: id (UUID) — update only. Fill id from an export.", updateOnly: true },
  { id: "products", label: "Pantry & Gifting", note: "Key: code. Supports create + update.", updateOnly: false },
];

function ImportTab() {
  const [entity, setEntity] = useState<ImportEntity>("dishes");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);
  const doPreview = useServerFn(previewBulkImport);
  const doApply = useServerFn(applyBulkImport);
  const doDiscard = useServerFn(discardBulkImport);

  const previewMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Choose a workbook first");
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = "";
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      const base64 = btoa(bin);
      return doPreview({ data: { entity, filename: file.name, base64 } });
    },
    onSuccess: (r) => {
      setPreview(r);
      setApplyResult(null);
      toast.success(`Preview ready — ${r.total} row${r.total === 1 ? "" : "s"}`);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "unknown_error";
      toast.error(`Preview failed: ${msg}`);
    },
  });

  const applyMut = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error("Run preview first");
      return doApply({ data: { import_id: preview.import_id } });
    },
    onSuccess: (r) => {
      setApplyResult(r);
      toast.success(`Applied ${r.applied} · Failed ${r.failed} · Skipped ${r.skipped}`);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "unknown_error";
      toast.error(`Apply failed: ${msg}`);
    },
  });

  const discardMut = useMutation({
    mutationFn: async () => {
      if (!preview) return;
      await doDiscard({ data: { import_id: preview.import_id } });
    },
    onSuccess: () => {
      setPreview(null);
      setApplyResult(null);
      setFile(null);
      const inp = document.getElementById("import-file") as HTMLInputElement | null;
      if (inp) inp.value = "";
      toast.success("Preview discarded");
    },
  });

  const canApply = preview && preview.counts.error === 0 && (preview.counts.create + preview.counts.update) > 0 && !applyResult;

  return (
    <div className="space-y-4">
      <Alert>
        <AlertDescription className="text-xs">
          Upload → validate metadata → validate columns → validate rows →
          preview → confirm. Nothing changes in the catalogue until you press
          Apply. Templates from the Export tab include the required hidden
          <code className="mx-1">_meta</code> sheet.
        </AlertDescription>
      </Alert>

      {IMPORT_ENTITIES.find((e) => e.id === entity)?.updateOnly && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden />
          <AlertDescription className="text-xs">
            <strong>Update-only module.</strong> New rows cannot be created via
            import. Every row must include a valid <code>id</code> copied from
            the export sheet. Rows with a blank or unknown id will be rejected.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileUp className="h-4 w-4" aria-hidden />
            <CardTitle>Upload workbook</CardTitle>
          </div>
          <CardDescription>
            Select the target module, then pick the .xlsx file you filled in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-[220px_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="import-entity">Module</Label>
              <select
                id="import-entity"
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={entity}
                onChange={(e) => {
                  setEntity(e.target.value as ImportEntity);
                  setPreview(null);
                  setApplyResult(null);
                }}
                disabled={previewMut.isPending || applyMut.isPending}
              >
                {IMPORT_ENTITIES.map((e) => (
                  <option key={e.id} value={e.id}>{e.label}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                {IMPORT_ENTITIES.find((e) => e.id === entity)?.note}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="import-file">Workbook</Label>
              <Input
                id="import-file"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setPreview(null);
                  setApplyResult(null);
                }}
                disabled={previewMut.isPending || applyMut.isPending}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => previewMut.mutate()}
              disabled={!file || previewMut.isPending || applyMut.isPending}
            >
              {previewMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
              Validate & preview
            </Button>
            {preview && (
              <Button
                variant="ghost"
                onClick={() => discardMut.mutate()}
                disabled={discardMut.isPending || applyMut.isPending}
              >
                Discard preview
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {preview && (
        <PreviewPanel
          preview={preview}
          canApply={!!canApply}
          applying={applyMut.isPending}
          onApply={() => applyMut.mutate()}
          applyResult={applyResult}
        />
      )}
    </div>
  );
}

function PreviewPanel({
  preview,
  canApply,
  applying,
  onApply,
  applyResult,
}: {
  preview: PreviewResult;
  canApply: boolean;
  applying: boolean;
  onApply: () => void;
  applyResult: ApplyResult | null;
}) {
  const { counts } = preview;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>Preview — {preview.entity}</CardTitle>
          <Badge variant="outline">v{preview.template_version}</Badge>
          <Badge variant="secondary">{preview.total} rows</Badge>
        </div>
        <CardDescription>
          File: <code>{preview.filename}</code>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Badge className="bg-emerald-100 text-emerald-900">Create {counts.create}</Badge>
          <Badge className="bg-blue-100 text-blue-900">Update {counts.update}</Badge>
          <Badge className="bg-amber-100 text-amber-900">Skip {counts.skip}</Badge>
          <Badge className="bg-red-100 text-red-900">Errors {counts.error}</Badge>
        </div>
        {preview.extra_columns.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" aria-hidden />
            <AlertDescription className="text-xs">
              Ignored unknown columns: {preview.extra_columns.join(", ")}
            </AlertDescription>
          </Alert>
        )}

        <div className="max-h-[420px] overflow-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/50">
              <tr>
                <th className="p-2 text-left">Row</th>
                <th className="p-2 text-left">Action</th>
                <th className="p-2 text-left">Key</th>
                <th className="p-2 text-left">Issues</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.row_number} className="border-t">
                  <td className="p-2 align-top">{r.row_number}</td>
                  <td className="p-2 align-top">
                    <Badge
                      variant={r.action === "error" ? "destructive" : "outline"}
                      className="text-[10px]"
                    >
                      {r.action}
                    </Badge>
                  </td>
                  <td className="p-2 align-top font-mono">{r.key_value ?? "—"}</td>
                  <td className="p-2 align-top text-red-700">
                    {r.errors.length > 0
                      ? r.errors.join("; ")
                      : r.warnings.length > 0
                      ? <span className="text-amber-700">{r.warnings.join("; ")}</span>
                      : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={onApply}
            disabled={!canApply || applying}
            aria-busy={applying}
          >
            {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Apply changes
          </Button>
          {counts.error > 0 && (
            <span className="text-xs text-red-700">
              Fix the errored rows in the source workbook and re-upload before applying.
            </span>
          )}
          {applyResult && (
            <span className="text-xs text-muted-foreground">
              Applied {applyResult.applied} · Failed {applyResult.failed} · Skipped {applyResult.skipped}
            </span>
          )}
        </div>

        {applyResult && applyResult.failures.length > 0 && (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">
              <strong>Failures:</strong>
              <ul className="mt-1 list-inside list-disc">
                {applyResult.failures.map((f) => (
                  <li key={f.row_number}>Row {f.row_number}: {f.error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

const ENTITIES: Array<{ id: Entity; label: string; description: string }> = [
  { id: "dishes", label: "Meal Plan Dishes", description: "All rotation dishes across breakfast, lunch, snacks and dinner." },
  { id: "beverages", label: "Beverages", description: "À-la-carte beverage catalogue with prices in paise." },
  { id: "soups", label: "Soups", description: "À-la-carte soup catalogue with prices in paise." },
  { id: "salads", label: "Salads", description: "À-la-carte salad catalogue with prices in paise." },
  { id: "products", label: "Pantry & Gifting", description: "Product cards shown on the Pantry & Gifting page." },
];

function ExportTab() {
  return (
    <div className="space-y-4">
      <Alert>
        <AlertDescription className="text-xs">
          Templates ship with a header row, one example row and a Notes sheet
          documenting every column. Exports include all live rows for the
          entity (admin view — not filtered by is_public / is_active).
        </AlertDescription>
      </Alert>
      <div className="grid gap-4 md:grid-cols-2">
        {ENTITIES.map((e) => (
          <EntityExportCard key={e.id} entity={e} />
        ))}
      </div>
    </div>
  );
}

function EntityExportCard({ entity }: { entity: { id: Entity; label: string; description: string } }) {
  const dlTemplate = useServerFn(downloadCatalogueTemplate);
  const doExport = useServerFn(exportCatalogue);
  const [busy, setBusy] = useState<"template" | "export" | null>(null);
  const [lastCount, setLastCount] = useState<number | null>(null);

  async function trigger(kind: "template" | "export") {
    setBusy(kind);
    try {
      const res = kind === "template"
        ? await dlTemplate({ data: { entity: entity.id } })
        : await doExport({ data: { entity: entity.id } });
      triggerDownload(res.filename, res.content_type, res.base64);
      if (kind === "export" && "row_count" in res && typeof res.row_count === "number") {
        const n = res.row_count;
        setLastCount(n);
        toast.success(`Exported ${n} ${entity.label.toLowerCase()} row${n === 1 ? "" : "s"}`);
      } else {
        toast.success("Template downloaded");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "unknown_error";
      toast.error(`${kind === "template" ? "Template" : "Export"} failed: ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4" aria-hidden />
          <CardTitle>{entity.label}</CardTitle>
        </div>
        <CardDescription>{entity.description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          onClick={() => trigger("template")}
          disabled={busy !== null}
        >
          {busy === "template"
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            : <Download className="mr-2 h-4 w-4" aria-hidden />}
          Download template
        </Button>
        <Button onClick={() => trigger("export")} disabled={busy !== null}>
          {busy === "export"
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            : <Download className="mr-2 h-4 w-4" aria-hidden />}
          Export current data
        </Button>
        {lastCount !== null && (
          <span className="text-xs text-muted-foreground">
            Last export: {lastCount} row{lastCount === 1 ? "" : "s"}
          </span>
        )}
      </CardContent>
    </Card>
  );
}

function triggerDownload(filename: string, contentType: string, base64: string) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function UrlIngestCard() {
  const ingest = useServerFn(ingestImageFromUrl);
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<IngestResult | null>(null);

  const m = useMutation({
    mutationFn: async (u: string) => await ingest({ data: { url: u } }),
    onSuccess: (r) => {
      setResult(r);
      toast.success("Image ingested from URL");
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "unknown_error";
      toast.error(`Ingest failed: ${msg}`);
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4" aria-hidden />
          <CardTitle>Ingest from URL</CardTitle>
        </div>
        <CardDescription>
          Paste a public HTTPS image URL. Private / loopback / link-local hosts are blocked.
          PNG, JPEG or WebP · min 200 × 200 · max 8 MB.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="ingest-url">Image URL</Label>
          <Input
            id="ingest-url"
            type="url"
            placeholder="https://example.com/photo.jpg"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
        <Button
          onClick={() => m.mutate(url.trim())}
          disabled={!url.trim() || m.isPending}
        >
          {m.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          Ingest
        </Button>
        {result && (
          <IngestResultView
            result={result}
            onClear={() => {
              setResult(null);
              setUrl("");
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function FileUploadCard() {
  const reserve = useServerFn(createSignedUploadUrl);
  const finalise = useServerFn(finaliseUploadedImage);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<IngestResult | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleUpload() {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File exceeds 8 MB limit");
      return;
    }
    setBusy(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "bin";
      const key = `${new Date().getUTCFullYear()}/${String(new Date().getUTCMonth() + 1).padStart(2, "0")}/${crypto.randomUUID().replace(/-/g, "")}.${ext}`;
      const signed = await reserve({ data: { bucket: "bulk-images", object_key: key } });
      const put = await fetch(signed.signed_url, {
        method: "PUT",
        headers: { "content-type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!put.ok) throw new Error(`upload_http_${put.status}`);
      const r = await finalise({ data: { bucket: "bulk-images", object_key: signed.path } });
      setResult(r);
      toast.success("Image uploaded and validated");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "unknown_error";
      toast.error(`Upload failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <UploadCloud className="h-4 w-4" aria-hidden />
          <CardTitle>Upload from device</CardTitle>
        </div>
        <CardDescription>
          PNG, JPEG or WebP · min 200 × 200 · max 8 MB. The server re-validates
          magic bytes after upload and deletes the object if it fails.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="upload-file">Image file</Label>
          <Input
            id="upload-file"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <Button onClick={handleUpload} disabled={!file || busy}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          Upload
        </Button>
        {result && (
          <IngestResultView
            result={result}
            onClear={() => {
              setResult(null);
              setFile(null);
              const input = document.getElementById("upload-file") as HTMLInputElement | null;
              if (input) input.value = "";
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function IngestResultView({ result, onClear }: { result: IngestResult; onClear: () => void }) {
  const [copied, setCopied] = useState(false);
  const previewUrl = useResolvedImageUrl(result.storage_ref);
  const formatLabel = result.content_type.replace("image/", "").toUpperCase();
  const sizeKb = Math.round(result.bytes / 1024);
  const sizeLabel = sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(2)} MB` : `${sizeKb} KB`;
  const copy = async () => {
    await navigator.clipboard.writeText(result.storage_ref);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Reference copied to clipboard");
  };
  return (
    <Alert>
      <AlertDescription className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary">{formatLabel}</Badge>
            <Badge variant="outline">{result.width}×{result.height}</Badge>
            <Badge variant="outline">{sizeLabel}</Badge>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClear}
            aria-label="Clear result"
          >
            <X className="h-4 w-4" aria-hidden />
            <span className="ml-1 text-xs">Clear</span>
          </Button>
        </div>
        <div className="overflow-hidden rounded-md border bg-muted">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Uploaded preview"
              className="mx-auto max-h-64 w-auto object-contain"
            />
          ) : (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <ImageOff className="mr-2 h-4 w-4" aria-hidden />
              <span className="text-xs">Loading preview…</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">
            {result.storage_ref}
          </code>
          <Button size="sm" variant="outline" onClick={copy}>
            {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            <span className="ml-1 text-xs">{copied ? "Copied" : "Copy"}</span>
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Paste this reference into any existing image field (dish, product,
          beverage, testimonial, homepage). The customer site resolves it to a
          signed URL automatically.
        </p>
      </AlertDescription>
    </Alert>
  );
}